import type { PreBookingRequest, PreBookingResponse } from '~/api/prebooking/models'
import { checkIsWebView, getAppVersion } from '@camelot/webview-utils'
import * as Sentry from '@sentry/vue'
import { getTime } from 'date-fns'
import { defineStore, storeToRefs } from 'pinia'
import { ResponseError } from '~/api/prebooking'
import { ExternalControllerApi } from '~/api/prebooking/apis/ExternalControllerApi'
import { PreBookingRequestVehicleCategoryEnum } from '~/api/prebooking/models'
import { OfferToJSON } from '~/api/pricing-offers'
import { AB_TEST_VARIANTS, isV3FunnelPath } from '~/composables/config/ab-test'
import { toRentalInstant } from '~/composables/prebooking-date'
import { trackAppsflyerPrebookingSuccess } from '~/composables/tracking/appsflyer'
import { useDataLayer } from '~/composables/tracking/datalayer'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useAbTestStore } from '~/stores/ab-test'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { usePaymentProfileStore } from '~/stores/payment-profile'
import { usePersonalStore } from '~/stores/personal'
import { useReferralStore } from '~/stores/referral'
import { useVehiclesStore } from '~/stores/vehicles'
import { getAppPlatform } from '~/utils/api-utils'

const prebookingApi = new ExternalControllerApi()

// We differentiate between not registered booking ("web") and webview based booking with token ("app")
export enum PrebookingType {
  WEB = 'web',
  APP = 'app',
}

// In the v3 funnel (/webview/<lang>/<city>/v3) the version is prebooking-v3-remove-redundants. Only when the app put
// the user into an A/B test, its tag takes over (e.g. variant_a_no_price_in_cta) so it reaches the datalake with the booking.
export function getWebviewVersion(): string {
  if (typeof window === 'undefined' || !isV3FunnelPath(window.location.pathname))
    return 'prebooking-v1'
  const { param } = useAbTestStore()
  return param ? AB_TEST_VARIANTS[param].tag : 'baseline'
}

function getApplicationVersion(): string {
  if (typeof window !== 'undefined') {
    return checkIsWebView(window) ? getAppVersion(window) : ''
  }
  return ''
}

function getPlatform(): string {
  if (typeof window !== 'undefined') {
    return checkIsWebView(window) ? getAppPlatform(window) : 'Web'
  }
  return 'Web'
}

export const usePrebookingCreationStore = defineStore('prebooking-creation', () => {
  const errorPrebookingCreation = ref<string | undefined>(undefined)

  const {
    leadGenerator,
    partnerBookingId,
    leadPlatform,
  } = storeToRefs(useReferralStore())
  const { selectedOffer } = storeToRefs(useOfferStore())
  const {
    startDate,
    endDate,
  } = storeToRefs(useDateTimeStore())
  const {
    address,
    city,
  } = storeToRefs(useLocationStore())
  const {
    emailAddress,
    firstName,
    lastName,
    countryOfResidence,
    phoneNumber,
  } = storeToRefs(usePersonalStore())
  const { additionalProperties } = storeToRefs(useExtrasStore())

  const additionalDriverCode = ref<string | undefined>(undefined)

  async function createPrebooking(type: PrebookingType, paymentAuthorizationId: string | undefined = undefined): Promise<PreBookingResponse | undefined> {
    try {
      const request = buildPrebookingRequest(type, paymentAuthorizationId)
      const createdPrebooking: PreBookingResponse = await prebookingApi.createPreBooking({ preBookingRequest: request })
      usePersonalStore().setIsNewCustomer(createdPrebooking.shouldRegister)
      useDataLayer().pushSuccessfulPrebookingEvent(createdPrebooking)
      trackAppsflyerPrebookingSuccess()
      return createdPrebooking
    }
    catch (err: any) {
      handlePrebookingError(err, type)
      return undefined
    }
  }

  function getWebBookingSpecificData(): Partial<PreBookingRequest> {
    return {
      email: emailAddress?.value?.toLowerCase(),
      firstName: firstName?.value,
      lastName: lastName?.value,
      countryOfResidence: countryOfResidence?.value,
      phoneNumber: phoneNumber?.value || undefined,
    }
  }

  function getAppBookingSpecificData(): Partial<PreBookingRequest> {
    return {
      uuid: useAppStore().customerUuid,
      paymentProfileId: usePaymentProfileStore().selectedPaymentProfile?.paymentProfileId,
      selectedOfferToken: useOfferStore().selectedOffer?.offerIdentifier,
    }
  }

  function getCommonRequestData(): Partial<PreBookingRequest> {
    const category = useVehiclesStore().getVehicleCategoryData()?.vehicleCategory
    const selectedOfferToken = selectedOffer.value?.offerIdentifier
    usePersonalStore().normalizeInputs()

    return {
      address: address.value?.deliveryAddress || (address.value?.info ?? ''),
      latitude: address.value?.location?.latitude ?? -1,
      longitude: address.value?.location?.longitude ?? -1,
      locationId: city.value?.locationId,
      vehicleCategory: PreBookingRequestVehicleCategoryEnum[category!],
      rentalStartsAtMillis: getTime(toRentalInstant(startDate.value?.date ?? new Date())),
      rentalEndsAtMillis: getTime(toRentalInstant(endDate.value?.date ?? new Date())),
      selectedOfferToken,
      decodedOfferToken: JSON.stringify({ offer: OfferToJSON(selectedOffer?.value) }),
      language: useTranslations().language.value,
      partnerBookingId: partnerBookingId?.value,
      leadGenerator: leadGenerator?.value,
      leadPlatform: leadPlatform?.value,
      selectedExtraIds: getSelectedExtraIds(),
      webviewVersion: getWebviewVersion(),
      appVersion: getApplicationVersion(),
      platform: getPlatform(),
    }
  }

  function buildPrebookingRequest(type: PrebookingType, paymentAuthorizationId: string | undefined = undefined): PreBookingRequest {
    const commonData = getCommonRequestData()
    const bookingSpecificData = type === PrebookingType.WEB ? getWebBookingSpecificData() : getAppBookingSpecificData()
    const creditBackApplied = Boolean(usePaymentProfileStore().activeCreditBack) || undefined
    return <PreBookingRequest>{
      ...commonData,
      ...bookingSpecificData,
      paymentAuthorizationId,
      creditBackApplied,
    }
  }

  function clearErrorToast() {
    errorPrebookingCreation.value = undefined
  }

  function getSelectedExtraIds(): number[] {
    const config = useRuntimeConfig().public
    const comfortPackageId = Number(config.comfortPackageId) || 7
    const basicCoverageId = Number(config.basicCoverageId) || 4
    const plusCoverageId = Number(config.plusCoverageId) || 6

    const extraIds: Set<number> = additionalProperties.value
    const { isMetaFlow } = useReferralStore()
    const metaOffersEnabled = config.META_OFFERS_ENABLED && isMetaFlow

    // Only auto-add basic coverage for non-B2B flows
    // Business profiles have special coverage arrangements and should not get auto-added coverage
    const isBusinessProfile = usePaymentProfileStore().selectedPaymentProfile?.type === 'BUSINESS'
    if (!extraIds.has(plusCoverageId) && !isBusinessProfile) {
      // if plus coverage { id=2 } is not selected - add basic coverage:
      const basicCoverageAdditionalOptionId: number = selectedOffer.value?.additionalOptions
        ?.find(option => new Set(option.tags).has('basic_insurance') || option.identifier === 'shield')
        ?.id
        ?? basicCoverageId
      extraIds.add(basicCoverageAdditionalOptionId)
    }

    if (metaOffersEnabled) {
      extraIds.add(comfortPackageId)
    }

    return Array.from(extraIds)
  }

  function handlePrebookingError(err: any, type: PrebookingType) {
    const user = {
      email: emailAddress?.value,
      username: lastName?.value,
    }
    const extra = {
      startDate: startDate.value.date,
      endDate: endDate.value.date,
    }

    if (err instanceof ResponseError) {
      handleResponseError(err, type, user, extra)
    }
    else {
      handleGenericError(err, type, user, extra)
    }
  }

  function handleResponseError(err: ResponseError, type: PrebookingType, user: any, extra: any) {
    if (err.response.status === 406) {
      // For APP (MCS subscriptions): already has active subscription - use similar_booking message
      // For WEB (LTCS): overlapping booking - keep existing overlapping_booking message
      errorPrebookingCreation.value = type === PrebookingType.APP
        ? translate('api_errors_similar_booking')
        : translate('api_errors_overlapping_booking')
    }
    else {
      errorPrebookingCreation.value = translate('api_errors_generic_error')
      Sentry.captureMessage(
        `‼️ Could not CREATE prebooking for ${type.toString()}: ${err?.message} ( ${err?.response?.statusText} ${err?.response?.status} )`,
        {
          user,
          level: 'error',
          extra,
          tags: {
            operation: 'create-prebooking',
            httpErrorCode: `${err?.response?.status}`,
          },
        },
      )
    }
  }

  function handleGenericError(err: any, type: PrebookingType, user: any, extra: any) {
    errorPrebookingCreation.value = translate('api_errors_generic_error')
    Sentry.captureMessage(
      `‼️ Could not CREATE prebooking for ${type.toString()}: ${err?.message} ( ${err?.response?.statusText} ${err?.response?.status} )`,
      {
        user,
        level: 'error',
        extra,
        tags: {
          operation: 'create-prebooking',
          httpErrorCode: `${err?.response?.status}`,
        },
      },
    )
  }

  return {
    additionalDriverCode,
    createPrebooking,
    errorPrebookingCreation,
    clearErrorToast,
    handlePrebookingError,
  }
})
